import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { PRESETS, CATEGORIES } from "@/lib/presets";
import { loadFont } from "@/lib/fonts";

const PAGE = 24;

const PresetCard = ({ p, active, onClick }) => {
  const s = p.style;
  const hi = ["karaoke", "word-color", "word-pop", "word-box"].includes(s.animation);
  const bg = s.bgMode !== "none" ? s.bgColor + Math.round(s.bgOpacity * 255).toString(16).padStart(2, "0") : "transparent";
  const txt = {
    fontFamily: `"${s.font}", "Noto Sans Devanagari", sans-serif`, fontWeight: s.weight, color: s.color, fontSize: 17,
    WebkitTextStroke: s.outlineWidth ? `${Math.max(1, s.outlineWidth / 4)}px ${s.outlineColor}` : undefined, paintOrder: "stroke fill",
    textShadow: s.shadowBlur ? `0 ${s.shadowY}px ${s.shadowBlur / 2}px ${s.shadowColor}` : undefined,
    textTransform: s.uppercase ? "uppercase" : "none", background: bg, padding: s.bgMode !== "none" ? "1px 6px" : 0, borderRadius: 4,
  };
  return (
    <button onClick={onClick} aria-pressed={active} data-testid={`preset-${p.id}`} title={p.name}
      className={`rounded-lg border p-2 text-left bg-[#2a2f3a] hover:border-[var(--cc-accent)] transition-colors ${active ? "border-[var(--cc-accent)] ring-1 ring-[var(--cc-accent)]" : "border-[var(--cc-border)]"}`}>
      <div className="h-12 grid place-items-center overflow-hidden rounded bg-[linear-gradient(135deg,#3b4252,#1c1f26)]">
        <span style={txt} className="whitespace-nowrap">नमस्ते <span style={hi ? { color: s.highlightColor } : undefined}>Hello</span></span>
      </div>
      <p className="text-[11px] font-semibold mt-1.5 truncate">{p.name}</p>
      <p className="text-[10px] text-[var(--cc-muted)] truncate">{p.cat} · {s.font}</p>
    </button>
  );
};

export const PresetGrid = ({ style, setStyle }) => {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [activeId, setActiveId] = useState(PRESETS[0].id);
  const list = useMemo(() => PRESETS.filter((p) => (cat === "All" || p.cat === cat) && (!q || `${p.name} ${p.style.font} ${p.style.animation}`.toLowerCase().includes(q.toLowerCase()))), [cat, q]);
  const shown = list.slice(0, limit);

  useEffect(() => { setLimit(PAGE); }, [cat, q]);
  useEffect(() => { [...new Set(shown.map((p) => p.style.font))].forEach(loadFont); }, [shown]);

  return (
    <div className="space-y-3" data-testid="preset-gallery">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold tracking-wider uppercase text-[var(--cc-text-2)]">Presets · {PRESETS.length}</h2>
      </div>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-2.5 top-3 text-[var(--cc-muted)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search styles, fonts, animations…" aria-label="Search styles" data-testid="preset-search"
          className="w-full h-10 pl-8 pr-2 rounded-md bg-[var(--cc-elev)] border border-[var(--cc-border-strong)] text-sm" />
      </div>
      <div className="flex gap-1.5 overflow-x-auto cc-scroll pb-1" role="tablist" aria-label="Style categories">
        {CATEGORIES.map((c) => (
          <button key={c} role="tab" aria-selected={cat === c} onClick={() => setCat(c)} data-testid={`preset-cat-${c.toLowerCase()}`}
            className={`h-8 px-3 shrink-0 rounded-full text-xs font-semibold border ${cat === c ? "bg-[var(--cc-accent)] text-black border-[var(--cc-accent)]" : "border-[var(--cc-border-strong)] text-[var(--cc-text-2)]"}`}>{c}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {shown.map((p) => <PresetCard key={p.id} p={p} active={activeId === p.id} onClick={() => { setActiveId(p.id); setStyle({ ...p.style }); }} />)}
      </div>
      {!list.length && <p className="text-sm text-[var(--cc-text-2)]">No styles match.</p>}
      {list.length > limit && (
        <button onClick={() => setLimit(limit + PAGE)} data-testid="preset-show-more" className="w-full h-10 rounded-md border border-[var(--cc-border-strong)] text-sm font-semibold text-[var(--cc-text-2)] hover:text-white">
          Show more ({list.length - limit} left)
        </button>
      )}
    </div>
  );
};
